import { isEmpty } from 'lodash-es'
import moment from 'moment-timezone'

import { isCalendlyMeeting } from './meeting_helpers'

const LOCATION_TYPES = [
  'gotomeeting',
  'gotomeeting_conference',
  'zoom',
  'zoom_conference',
  'google_conference',
  'microsoft_teams_conference',
  'webex_conference'
]

export const validatedExternalLocation = (event) => LOCATION_TYPES.includes(event.locationType) && event.externalLocation

export const hasEventLocationIssue = (event) => {
  const externalLocation = validatedExternalLocation(event)
  return externalLocation && externalLocation.showLocationIssue
}

export const hasEventPaymentIssue = ({ hasUncapturedCharges }) => hasUncapturedCharges

export const isCanceledEvent = ({ cancelled, status }) => cancelled || status === 'CANCELED'

export const isExpiredEvent = ({ expired }) => expired

export const eventHasInternalConflict = (event, { otherEventsOnDay }, isMyCalendlyScope) => {
  if (isEmpty(otherEventsOnDay)) return false
  if (!isMyCalendlyScope) return false
  if (!isCalendlyMeeting(event)) return false

  const { startTime, endTime, uuid } = event
  const eventStart = moment(startTime)
  const eventEnd = moment(endTime)

  return otherEventsOnDay.some((otherEvent) => {
    if (otherEvent.uuid === uuid) return false
    const otherEventStart = moment(otherEvent.startTime)
    const otherEventEnd = moment(otherEvent.endTime)
    // See if the current event overlaps another event in anyway, including being completely during
    if (eventStart.isBetween(otherEventStart, otherEventEnd, 'minute')
      || eventEnd.isBetween(otherEventStart, otherEventEnd, 'minute')) return true

    // Final check to see if other event is completely during the current event
    return eventStart.isSameOrBefore(otherEventStart, 'minute') && eventEnd.isSameOrAfter(otherEventEnd, 'minute')
  })
}

export const allInviteesDeclined = (event) => {
  if (event.cancellation?.canceledByInvitee) return true
  if (!event.attendees?.length) return false
  const attendeesWithRsvp = event.attendees.filter((attendee) => attendee.rsvp !== undefined)
  if (!attendeesWithRsvp.length) return false
  return attendeesWithRsvp.every((attendee) => attendee.rsvp === 'declined')
}

export const didInviteeNoShow = ({ invitee, status }) => invitee?.noShow || status === 'NO_SHOW'

export const wasEventRescheduled = ({ oldSchedulable, status }) => !isEmpty(oldSchedulable) || status === 'RESCHEDULED'

export const hasEventPassed = ({ past }) => past

export const isEventInProgress = (event, now = moment()) => {
  if (!event.startTime || !event.endTime) return false

  const start = moment(event.startTime)
  const end = moment(event.endTime)

  return now.isBetween(start, end)
}

export const isEventToday = (event, now = moment()) => {
  if (!event.startTime) return false

  const start = moment(event.startTime)
  return now.isSame(start, 'day')
}

export const isEventInTheFuture = (event, now = moment()) => {
  if (!event.startTime) return false

  const start = moment(event.startTime)
  return now.isBefore(start)
}

export const isEventNext = (event, { otherEventsOnDay, now = moment() } = {}) => {
  if (!event.startTime) return false
  if (isEmpty(otherEventsOnDay)) return true

  otherEventsOnDay = otherEventsOnDay
    .filter((e) => (
      !isCanceledEvent(e)
      && e.startTime
      && (isEventInProgress(e, now) || isEventInTheFuture(e, now))
    ))
    .map(({ startTime }) => moment(startTime))

  const thisEventStart = moment(event.startTime)

  return !otherEventsOnDay.some((otherDayEvent) => thisEventStart.isAfter(otherDayEvent))
}

const availableBadgeKeys = [
  {
    checkCondition: hasEventPaymentIssue,
    key: 'PAYMENT_ERROR'
  },
  {
    checkCondition: (event) => hasEventLocationIssue(event) && !hasEventPassed(event),
    key: 'ACTION_NEEDED'
  },
  {
    checkCondition: isExpiredEvent,
    key: 'EXPIRED'
  },
  {
    checkCondition: isCanceledEvent,
    key: 'CANCELLED'
  },
  {
    checkCondition: (event, options) => (
      !isCanceledEvent(event)
      && isEventToday(event, options.now)
      && isEventInTheFuture(event, options.now)
      && isEventNext(event, options)
    ),
    key: 'UP_NEXT'
  },
  {
    checkCondition: (event, options) => isEventInProgress(event, options.now),
    key: 'IN_PROGRESS'
  },
  {
    checkCondition: allInviteesDeclined,
    key: 'INVITEES_DECLINED'
  },
  {
    checkCondition: didInviteeNoShow,
    key: 'NO_SHOW'
  },
  {
    checkCondition: eventHasInternalConflict,
    key: 'CONFLICT'
  },
  {
    checkCondition: wasEventRescheduled,
    key: 'RESCHEDULED'
  },
  {
    checkCondition: hasEventPassed,
    key: 'PAST'
  },
]

export const getBadgeKey = (event, isMyCalendlyScope, { otherEventsOnDay, now: nowOverride } = {}) => {
  const now = nowOverride ?? moment()
  const options = { otherEventsOnDay, now }
  const badgeKey = availableBadgeKeys.find(({ checkCondition }) => checkCondition(event, options, isMyCalendlyScope))
  return badgeKey?.key
}
